import {
  Box,
  Button,
  Flex,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useTrackingNotifications, type TrackingNotification } from "hooks/useTrackingNotifications";
import { useCallback, useState } from "react";
import { showToast, toastTypes } from "utils";

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleString("fr-FR");
}

export default function TrackingNotificationsPage() {
  const toast = useToast();
  const { notifications, isLoading, error, refetch, markAsRead } = useTrackingNotifications();
  const [markingId, setMarkingId] = useState<string | null>(null);

  const handleMarkAsRead = useCallback(
    async (id: string) => {
      setMarkingId(id);
      try {
        await markAsRead(id);
        showToast({ toast, message: "Notification marquée comme lue.", status: toastTypes.success });
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || "Erreur.";
        showToast({ toast, message: msg, status: toastTypes.error });
      } finally {
        setMarkingId(null);
      }
    },
    [markAsRead, toast]
  );

  return (
    <TrackingPageLayout
      title="Notifications"
      action={
        <Button size="sm" onClick={() => refetch()} isLoading={isLoading}>
          Actualiser
        </Button>
      }
    >
      {error && (
        <Text color="red.600" mb={4} px={4} pt={4}>
          Erreur lors du chargement des notifications.
        </Text>
      )}
      {isLoading && (notifications?.length ?? 0) === 0 ? (
        <Flex py={8} justifyContent="center">
          <Spinner size="lg" />
        </Flex>
      ) : (notifications?.length ?? 0) === 0 ? (
        <Box p={8} textAlign="center">
          <Text color="gray.600">Aucune notification.</Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={3} p={4}>
          {(notifications as TrackingNotification[]).map((notif) => (
            <Box
              key={notif.id}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg={notif.is_read ? "gray.50" : "white"}
              shadow="sm"
            >
              <HStack justify="space-between" align="flex-start" mb={2}>
                <Text fontWeight="semibold" fontSize="md">
                  {notif.title || "Sans titre"}
                </Text>
                <HStack>
                  {notif.type_display && (
                    <Badge colorScheme="blue" fontSize="xs">
                      {notif.type_display}
                    </Badge>
                  )}
                  {!notif.is_read && (
                    <Badge colorScheme="green">Non lue</Badge>
                  )}
                </HStack>
              </HStack>
              <Text fontSize="sm" color="gray.700" mb={2}>
                {notif.message}
              </Text>
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.500">
                  {formatDate(notif.created_at)}
                </Text>
                {!notif.is_read && (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => handleMarkAsRead(notif.id)}
                    isLoading={markingId === notif.id}
                  >
                    Marquer comme lue
                  </Button>
                )}
              </HStack>
            </Box>
          ))}
        </VStack>
      )}
    </TrackingPageLayout>
  );
}
